import React, {useEffect, useMemo, useState} from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import WarningIcon from '@material-ui/icons/Warning';
import {Button, Box, CircularProgress, IconButton, makeStyles} from '@material-ui/core';
import ReactGA from 'react-ga4';
import fileDownload from 'js-file-download';
import FadeIn from 'react-fade-in';
import {APICall} from '../../../api/api';
import usePersistentAuthStore from '../../../store/persistentAuthStore';
import useStore from '../../../store/store';
import {AdsTxtLinesList} from './AdsTxtLinesList';
import {getMissingAdsTxtLines, getResellerLinesStatus} from './adsTxtHelpers';

const ResellerLinesStatusIcon = ({status}) => {
  if (!status) return <CircularProgress size={22} />;
  if (status === 'all_missing') return <ReportProblemIcon style={{color: '#f44336'}} />;
  if (status === 'partial') return <WarningIcon style={{color: '#ed6c02'}} />;
  return <CheckCircleIcon color="secondary" />;
};

const GridIContainer = props => <Grid container {...props} />;

const useStyles = makeStyles(theme => ({
  wrapper: {
    flexGrow: 1,
    borderRadius: '4px',
    border: '1px solid #7979793b',
    marginTop: 20,
    background: theme.palette.background.paper,
  },
  collapseContainer: {
    width: '100%',
  },
  listRow: {
    minHeight: 65,
    borderTop: '1px solid #7979793b',
  },
}));

export const EnterpriseResellerBlock = () => {
  const classes = useStyles();
  const {auth} = usePersistentAuthStore();
  const rows = useStore(state => state.resellerAdsTxtLines);
  const checked = useStore(state => state.checked);
  const domains = useStore(state => state.domains);
  const setState = useStore(state => state.setState);
  const [open, setOpen] = useState(false);
  const [domainsOpen, setDomainsOpen] = useState(false);
  const [openDomainId, setOpenDomainId] = useState(null);

  useEffect(() => {
    if (rows) return;

    APICall({
      endpoint: 'setupadResellerAdsTxtLines',
      options: {
        method: 'get',
        headers: {Authorization: `Bearer ${auth.token}`},
      },
    }).then(data => {
      if (data?.success) setState('resellerAdsTxtLines', data.rows);
    });
  }, [rows, auth.token, setState]);

  const filteredLines = rows?.filter(line => line !== '') ?? [];
  const domainsChecked = domains.length > 0 && checked.length === domains.length;

  const domainStatuses = useMemo(() => {
    if (!filteredLines.length || !domains?.length) return [];

    return domains.map(domain => {
      const checkedDomain = checked.find(item => item.id === domain.id);
      const missingResellerLines = checkedDomain
        ? getMissingAdsTxtLines(filteredLines, checkedDomain.currentAdsTXT?.rows)
        : null;

      return {domain, checkedDomain, missingResellerLines};
    });
  }, [checked, domains, filteredLines]);

  const downloadAdsTxt = () => {
    fileDownload(filteredLines.join('\r\n'), 'ads.txt');
    ReactGA.event({
      category: 'Ads.txt',
      action: 'click',
      label: 'download reseller ads.txt',
    });
  };

  const getDomainStatusLabel = ({checkedDomain, missingResellerLines}) => {
    if (!checkedDomain) return 'Checking...';
    if (!checkedDomain.currentAdsTXT?.success) {
      return `${missingResellerLines.length} ${
        missingResellerLines.length === 1 ? 'line' : 'lines'
      } missing — ads.txt not found`;
    }
    if (missingResellerLines.length === 0) return 'All reseller lines present';
    return `${missingResellerLines.length}  ${
      missingResellerLines.length === 1 ? 'line' : 'lines'
    } missing`;
  };

  const domainsAggregateStatus = useMemo(() => {
    if (!domainsChecked) return null;
    const statuses = domainStatuses.map(({checkedDomain}) =>
      checkedDomain
        ? getResellerLinesStatus(filteredLines, checkedDomain.currentAdsTXT?.rows)
        : null
    );
    if (statuses.includes('all_missing')) return 'all_missing';
    if (statuses.includes('partial')) return 'partial';
    return 'complete';
  }, [domainStatuses, domainsChecked, filteredLines]);

  const domainsSummary = useMemo(() => {
    if (!domains.length) return '';
    if (!domainsChecked) return 'Checking domain status...';
    const needsUpdate = domainStatuses.filter(({missingResellerLines}) => missingResellerLines?.length > 0)
      .length;
    if (needsUpdate === 0) {
      return `${domains.length} ${domains.length === 1 ? 'domain' : 'domains'} · all reseller lines present`;
    }
    return `${domains.length} ${domains.length === 1 ? 'domain' : 'domains'} · ${needsUpdate} need ${
      needsUpdate === 1 ? 'update' : 'updates'
    }`;
  }, [domainStatuses, domains.length, domainsChecked]);

  const toggleDomainsOpen = () => {
    setDomainsOpen(current => {
      if (current) setOpenDomainId(null);
      return !current;
    });
  };

  return (
    <div style={{marginTop: 50}}>
      <FadeIn>
        <Typography variant="h4" gutterBottom>
          Connect Setupad Reseller accounts
        </Typography>
        <Typography gutterBottom>
          Update your ads.txt with the additional lines and we will do the rest.
        </Typography>
        <Grid container className={classes.wrapper} spacing={1} direction="column">
          <List disablePadding component={GridIContainer}>
            <ListItem disableRipple className={classes.listRow}>
              <ListItemText
                primary={
                  rows ? (
                    <>
                      <Box component="span" fontWeight={700}>
                        {filteredLines.length}
                      </Box>
                      {` ads.txt ${filteredLines.length === 1 ? 'line' : 'lines'} required`}
                    </>
                  ) : (
                    'Loading lines...'
                  )
                }
              />

              {rows && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{marginLeft: 5, marginRight: 10}}
                  onClick={e => {
                    e.stopPropagation();
                    downloadAdsTxt();
                  }}
                >
                  Download Setupad Reseller Ads.txt
                </Button>
              )}

              {!rows ? (
                <CircularProgress size={22} />
              ) : (
                <IconButton onClick={() => setOpen(!open)}>
                  {open ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </ListItem>

            {rows && (
              <Collapse in={open} timeout={0} classes={{container: classes.collapseContainer}}>
                <div style={{padding: 20}}>
                  <AdsTxtLinesList lines={rows} />
                </div>
              </Collapse>
            )}

            {rows && domains.length > 0 && (
              <>
                <ListItem
                  disableRipple
                  button
                  className={classes.listRow}
                  onClick={toggleDomainsOpen}
                >
                  <ListItemIcon>
                    {!domainsChecked ? (
                      <CircularProgress size={22} />
                    ) : (
                      <ResellerLinesStatusIcon status={domainsAggregateStatus} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      domainsChecked ? (
                        <>
                          <Box component="span" fontWeight={700}>
                            Domain status
                          </Box>
                          {` · ${domainsSummary}`}
                        </>
                      ) : (
                        'Domain status — Checking...'
                      )
                    }
                  />
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      toggleDomainsOpen();
                    }}
                  >
                    {domainsOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </ListItem>

                <Collapse
                  in={domainsOpen}
                  timeout={0}
                  classes={{container: classes.collapseContainer}}
                >
                  {!domainsChecked ? (
                    <ListItem disableRipple style={{minHeight: 65, borderTop: '1px solid #7979793b'}}>
                      <ListItemIcon>
                        <CircularProgress size={22} />
                      </ListItemIcon>
                      <ListItemText primary="Checking domains..." />
                    </ListItem>
                  ) : (
                    domainStatuses.map(({domain, checkedDomain, missingResellerLines}) => {
                      const hasMissing = missingResellerLines?.length > 0;
                      const isExpanded = openDomainId === domain.id;
                      const resellerStatus = checkedDomain
                        ? getResellerLinesStatus(filteredLines, checkedDomain.currentAdsTXT?.rows)
                        : null;

                      return (
                        <React.Fragment key={domain.id}>
                          <ListItem
                            disableRipple
                            button={hasMissing}
                            style={{
                              minHeight: 65,
                              borderTop: '1px solid #7979793b',
                            }}
                            onClick={
                              hasMissing
                                ? () => setOpenDomainId(isExpanded ? null : domain.id)
                                : undefined
                            }
                          >
                            <ListItemIcon>
                              {!checkedDomain ? (
                                <CircularProgress size={22} />
                              ) : (
                                <ResellerLinesStatusIcon status={resellerStatus} />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={domain.title}
                              secondary={getDomainStatusLabel({checkedDomain, missingResellerLines})}
                            />
                            {hasMissing && (
                              <IconButton
                                size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  setOpenDomainId(isExpanded ? null : domain.id);
                                }}
                              >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            )}
                          </ListItem>
                          {hasMissing && (
                            <Collapse
                              in={isExpanded}
                              timeout={0}
                              classes={{container: classes.collapseContainer}}
                            >
                              <div style={{padding: '0 20px 20px'}}>
                                <AdsTxtLinesList lines={missingResellerLines} />
                              </div>
                            </Collapse>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </Collapse>
              </>
            )}
          </List>
        </Grid>
      </FadeIn>
    </div>
  );
};
